import { useState } from 'react';
import SafeIcon from './SafeIcon';
import { useApp } from '../i18n/AppContext';
import type { Post } from '../types';

const captionFoldLengthByChannel: Record<Post['channel'], number> = {
  Instagram: 125,
  Facebook: 240,
  TikTok: 100,
  LinkedIn: 210,
};
const captionMaxLengthByChannel: Record<Post['channel'], number> = {
  Instagram: 2200,
  Facebook: 63206,
  TikTok: 2200,
  LinkedIn: 3000,
};

export default function PlatformPreview({
  post,
  image,
  caption,
  account,
}: {
  post: Post;
  image: string;
  caption: string;
  account: string;
}) {
  const { translate } = useApp();
  const [expanded, setExpanded] = useState(false);
  const foldLength = captionFoldLengthByChannel[post.channel];
  const maxLength = captionMaxLengthByChannel[post.channel];
  const captionIsLongerThanFold = caption.length > foldLength;
  const showTruncatedCaption = captionIsLongerThanFold && !expanded;
  const visibleCaption = showTruncatedCaption
    ? `${caption.slice(0, foldLength).trimEnd()}…`
    : caption;
  const moreOrLessToggle = captionIsLongerThanFold && (
    <button className="link-button pp-more" onClick={() => setExpanded(!expanded)}>
      {translate(expanded ? 'less' : 'more')}
    </button>
  );
  const avatar = (
    <span className="pp-avatar" aria-hidden="true">
      {account.replace('@', '').charAt(0).toUpperCase()}
    </span>
  );
  const isVideoFormat = post.format === 'Reel' || post.format === 'Story';
  const showsCaptionAboveMedia = post.channel === 'Facebook' || post.channel === 'LinkedIn';
  const headerSubtitle =
    post.channel === 'LinkedIn'
      ? translate('Company page · now')
      : post.channel === 'Facebook'
        ? translate('Just now · public')
        : '';
  const media =
    post.format === 'PDF' ? (
      <div className="pp-doc">
        <SafeIcon name="FiFileText" size={34} />
        <strong>{post.title}</strong>
        <span>{translate('Document · {n} pages', { n: 6 })}</span>
      </div>
    ) : (
      <div className="pp-media">
        <img src={image} alt={post.title} />
        {isVideoFormat && (
          <span className="pp-play">
            <SafeIcon name="FiPlay" size={26} />
          </span>
        )}
        {post.format === 'Carousel' && <span className="pp-count">1/3</span>}
      </div>
    );

  return (
    <figure
      className={`platform-preview pp-${post.channel.toLowerCase()} pp-${post.format.toLowerCase()}`}
      aria-label={translate('Preview as on {channel}', { channel: post.channel })}
    >
      {post.channel === 'TikTok' ? (
        <>
          {media}
          <div className="pp-overlay">
            <strong>{account}</strong>
            <p>
              {visibleCaption} {moreOrLessToggle}
            </p>
            <span className="pp-sound">
              <SafeIcon name="FiMusic" size={12} /> {translate('Original sound')}
            </span>
          </div>
          <div className="pp-rail" aria-hidden="true">
            <SafeIcon name="FiHeart" />
            <SafeIcon name="FiMessageCircle" />
            <SafeIcon name="FiShare2" />
          </div>
        </>
      ) : (
        <>
          <header className="pp-head">
            {avatar}
            <span>
              <strong>{account}</strong>
              <span className="muted">{headerSubtitle}</span>
            </span>
            <SafeIcon name="FiMoreHorizontal" />
          </header>
          {showsCaptionAboveMedia && (
            <p className="pp-caption">
              {visibleCaption} {moreOrLessToggle}
            </p>
          )}
          {media}
          <div className="pp-actions" aria-hidden="true">
            <FeedActionButtons channel={post.channel} />
          </div>
          {post.channel === 'Instagram' && (
            <p className="pp-caption">
              <strong>{account}</strong> {visibleCaption} {moreOrLessToggle}
            </p>
          )}
        </>
      )}
      <figcaption className="muted small-print">
        {caption.length > maxLength
          ? translate('Too long for {channel}: {n} of {max} characters.', {
              channel: post.channel,
              n: caption.length,
              max: maxLength,
            })
          : translate(
              '{n} characters · {channel} shows about {fold} before “more”. Simplified preview.',
              {
                n: caption.length,
                channel: post.channel,
                fold: foldLength,
              }
            )}
      </figcaption>
    </figure>
  );
}

function FeedActionButtons({ channel }: { channel: Post['channel'] }) {
  const { translate } = useApp();
  if (channel === 'Instagram')
    return (
      <>
        <SafeIcon name="FiHeart" />
        <SafeIcon name="FiMessageCircle" />
        <SafeIcon name="FiSend" />
        <span className="pp-spacer" />
        <SafeIcon name="FiBookmark" />
      </>
    );
  if (channel === 'LinkedIn')
    return (
      <>
        <span>
          <SafeIcon name="FiThumbsUp" size={14} /> {translate('Like')}
        </span>
        <span>
          <SafeIcon name="FiMessageSquare" size={14} /> {translate('Comment')}
        </span>
        <span>
          <SafeIcon name="FiRepeat" size={14} /> {translate('Repost')}
        </span>
      </>
    );
  return (
    <>
      <span>
        <SafeIcon name="FiThumbsUp" size={14} /> {translate('Like')}
      </span>
      <span>
        <SafeIcon name="FiMessageSquare" size={14} /> {translate('Comment')}
      </span>
      <span>
        <SafeIcon name="FiShare2" size={14} /> {translate('Share')}
      </span>
    </>
  );
}
